/* eslint-disable react/no-unescaped-entities */
/**
 * Changelog page (/changelog).
 *
 * Reads from src/data/changelog.js — pure read-only display. Pinned to the
 * sidebar via a "What's new" link. When a new release lands (data file
 * pushed) users see a small NEW dot in the sidebar until they visit the page.
 *
 * Admin-only items (`adminOnly: true` in the data) are hidden from regular
 * users entirely. Admins see them with a small "Admin" badge so they know
 * what's hidden from their customers.
 *
 * Pairs with `useChangelogSeen` (localStorage-backed) to clear the dot.
 */
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, Wrench, TrendingUp, ArrowRight, Rocket, Shield } from 'lucide-react';
import { CHANGELOG, LATEST_VERSION } from '../data/changelog';

const TYPE_META = {
  feature:     { icon: Sparkles,    label: 'New',         tone: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  improvement: { icon: TrendingUp,  label: 'Improved',    tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  fix:         { icon: Wrench,      label: 'Fixed',       tone: 'bg-amber-100 text-amber-800 border-amber-200' },
};

const formatDate = (iso) => {
  try {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso; }
};

export const ChangelogPage = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  // Mark the latest version as seen the moment the user lands on /changelog.
  // The sidebar badge reads this to decide whether to render the green dot.
  useEffect(() => {
    try {
      localStorage.setItem('changelog_seen_version', LATEST_VERSION);
      window.dispatchEvent(new Event('changelog-seen'));
    } catch {
      // localStorage write can fail in restricted contexts; the dot will
      // simply remain visible until the user can store the marker. Safe to ignore.
    }
  }, []);

  // Filter releases by audience. Regular users never see `adminOnly` items;
  // releases left with zero items are hidden entirely so the page doesn't
  // show empty cards.
  const visibleReleases = useMemo(() => {
    return CHANGELOG
      .map((release) => ({
        ...release,
        items: release.items.filter((it) => isAdmin || !it.adminOnly),
      }))
      .filter((release) => release.items.length > 0);
  }, [isAdmin]);

  const latestRelease = visibleReleases[0];

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="changelog-page">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
            <Rocket className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900">What's new</h1>
            <p className="text-sm text-zinc-500">Every notable change, fix, and feature shipped to PostToSocial.</p>
          </div>
        </div>
      </div>

      {/* Latest pinned banner — based on the first visible release for this
          audience, NOT the absolute latest CHANGELOG[0]. This keeps the
          banner truthful for non-admins when the absolute latest happens to
          be an admin-only release (banner would otherwise advertise content
          the user can't see). */}
      {latestRelease && (
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white" data-testid="changelog-latest-badge">
              Latest · v{latestRelease.version}
            </Badge>
            <p className="text-sm font-medium text-zinc-900 flex-1 min-w-0">
              {latestRelease.title}
            </p>
            <span className="text-xs text-zinc-500">{formatDate(latestRelease.date)}</span>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <div className="space-y-5">
        {visibleReleases.map((release, idx) => (
          <Card key={`${release.date}-${release.version || idx}`} data-testid={`changelog-release-${release.version || idx}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 pb-3">
                {release.version && (
                  <Badge variant="outline" className="font-mono text-[10px]">
                    v{release.version}
                  </Badge>
                )}
                <span className="text-xs text-zinc-500">{formatDate(release.date)}</span>
                {release.tag && (
                  <Badge variant="secondary" className="text-[10px]">{release.tag}</Badge>
                )}
                <h2 className="font-semibold text-zinc-900 w-full mt-1">{release.title}</h2>
              </div>
              <ul className="space-y-2">
                {release.items.map((it, i) => {
                  const meta = TYPE_META[it.type] || TYPE_META.improvement;
                  const Icon = meta.icon;
                  return (
                    <li key={i} className="flex flex-wrap items-start gap-2.5 text-sm text-zinc-700 leading-relaxed">
                      <Badge variant="outline" className={`shrink-0 gap-1 ${meta.tone}`} data-testid={`changelog-item-${it.type}`}>
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </Badge>
                      {/* Admin badge — only rendered for admins (since
                          non-admins never see adminOnly items). Tells the
                          admin "this row is hidden from regular users". */}
                      {isAdmin && it.adminOnly && (
                        <Badge variant="outline" className="shrink-0 gap-1 bg-zinc-100 text-zinc-600 border-zinc-200 text-[10px]" title="Hidden from regular users">
                          <Shield className="w-2.5 h-2.5" />
                          Admin
                        </Badge>
                      )}
                      <span className="flex-1 min-w-0">
                        {it.text}
                        {it.link && (
                          <Link
                            to={it.link.to}
                            className="ml-2 inline-flex items-center gap-0.5 text-indigo-600 hover:underline text-xs font-medium whitespace-nowrap"
                          >
                            {it.link.label} <ArrowRight className="w-3 h-3" />
                          </Link>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center pt-4 pb-2">
        <p className="text-xs text-zinc-400">
          Have feedback or a feature request?{' '}
          <Link to="/support" className="text-indigo-600 hover:underline">Open a ticket</Link>
        </p>
      </div>
    </div>
  );
};

export default ChangelogPage;
